import { Dispatch, FC, SetStateAction, useEffect, useMemo } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import {
  Box,
  FormControl,
  FormHelperText,
  MenuItem,
  Select,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers-pro";
import __ from "src/utils/Translations";
import generate24Hours from "src/utils/Company/Absences/generate24Hours";
import formatHourLabel from "src/utils/Company/Absences/formatHourLabel";
import { transformAbsencePlanToPlannedDays } from "src/utils/Company/Absences/transformAbsencePlan";
import CustomPickersDay from "src/Pages/Company/Absences/AbsenceRequest/AbsenceRequestDatepicker/CustomPickersDay";
import {
  IAbsencePlan,
  IAbsenceRequestDay,
} from "src/Pages/Company/Absences/AbsenceRequest/AbsenceRequest.models";
import { IExcludedDay } from "src/Pages/Company/Absences/Calendars/Calendars.models";
import {
  defaultBoxStyles,
  dateFieldStyles,
  hourFieldStyles,
  hourSelectStyles,
  hourOptionStyles,
  hourDividerStyles,
} from "./AbsenceHourPicker.styles";

interface IAbsenceHourPickerProps {
  absenceRequestDays: IAbsenceRequestDay[];
  excludedDays: IExcludedDay[];
  absencePlanData?: IAbsencePlan;
  multipleRequestsPerDayAllowed: boolean;
  billInCalendarDays: boolean;
  hoursCount: number;
  setHoursCount: Dispatch<SetStateAction<number>>;
  handleMonthChange: (month: Date) => void;
}

const AbsenceHourPicker: FC<IAbsenceHourPickerProps> = ({
  absenceRequestDays,
  excludedDays,
  absencePlanData,
  multipleRequestsPerDayAllowed,
  billInCalendarDays,
  hoursCount,
  setHoursCount,
  handleMonthChange,
}) => {
  const {
    control,
    formState: { errors },
    trigger,
  } = useFormContext();
  const options = generate24Hours();
  const startTime = useWatch({ name: "startTime" });
  const endTime = useWatch({ name: "endTime" });

  const plannedDays = useMemo(() => {
    if (!absencePlanData) return [];

    return transformAbsencePlanToPlannedDays(absencePlanData);
  }, [absencePlanData]);

  useEffect(() => {
    setHoursCount(Math.abs(endTime - startTime));
    trigger(["startTime", "endTime"]);
  }, [startTime, endTime, trigger, setHoursCount]);

  return (
    <Box sx={defaultBoxStyles}>
      <Controller
        control={control}
        name="date"
        render={({
          field: { value, ref, onChange },
          fieldState: { error },
        }) => (
          <DatePicker
            label={__("Wybierz datę")}
            sx={dateFieldStyles}
            value={value}
            onChange={onChange}
            onMonthChange={handleMonthChange}
            dayOfWeekFormatter={(date: Date) =>
              date.toLocaleString("pl", { weekday: "short" })
            }
            slots={{
              day: (props) =>
                CustomPickersDay({
                  absenceRequestDays,
                  excludedDays,
                  plannedDays,
                  multipleRequestsPerDayAllowed,
                  billInCalendarDays,
                  ...props,
                }),
            }}
            slotProps={{
              textField: {
                required: true,
                error: !!error,
                helperText: error?.message,
                fullWidth: true,
                ref,
              },
            }}
            data-testid="absence-hour-picker"
          />
        )}
      />

      <Stack
        direction="row"
        spacing={2}
        flexWrap="nowrap"
        useFlexGap
        sx={defaultBoxStyles}
      >
        <Controller
          name="startTime"
          control={control}
          render={({ field }) => (
            <FormControl sx={hourFieldStyles}>
              <Select
                {...field}
                id="startTime"
                sx={hourSelectStyles}
                data-testid="absence-hour-picker__start"
              >
                {options.map((option) => (
                  <MenuItem
                    sx={hourOptionStyles}
                    key={option.value}
                    value={option.value}
                  >
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        />
        <Typography component="span" sx={hourDividerStyles}>
          –
        </Typography>
        <Controller
          name="endTime"
          control={control}
          render={({ field }) => (
            <FormControl sx={hourFieldStyles}>
              <Select
                {...field}
                id="endTime"
                sx={hourSelectStyles}
                data-testid="absence-hour-picker__end"
              >
                {options.map((option) => (
                  <MenuItem
                    sx={hourOptionStyles}
                    key={option.value}
                    value={option.value}
                  >
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        />
        <Typography
          variant="body2"
          component="span"
          sx={{
            alignSelf: "center",
          }}
        >
          {__("SUMA")}: <strong>{formatHourLabel(hoursCount)}</strong>
        </Typography>
      </Stack>
      {errors?.startTime && (
        <FormHelperText error={!!errors?.startTime}>
          {`${errors?.startTime?.message}`}
        </FormHelperText>
      )}
    </Box>
  );
};

export default AbsenceHourPicker;
